import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLoginMenu, setShowLoginMenu] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowLoginMenu(false);
  }, [location]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: 'Accueil', path: '/' },
    { name: 'À propos', path: '/about' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'Produits', path: '/products' },
    { name: 'Qualité', path: '/quality' },
    { name: 'Partenaires', path: '/partners' },
    { name: 'Contact', path: '/contact' },
  ];

  const loginOptions = [
    {
      title: 'Administrateur',
      description: 'Gestion complète du site',
      icon: 'ri-admin-line',
      path: '/admin/login',
      color: 'text-[#E31B23]',
      bgColor: 'bg-[#E31B23]/10',
      hoverBg: 'hover:bg-[#E31B23]/20',
    },
    {
      title: 'Client',
      description: 'Demandes de devis et suivi',
      icon: 'ri-user-line',
      path: '/client/login',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      hoverBg: 'hover:bg-blue-100',
    },
    {
      title: 'Distributeur',
      description: 'Espace partenaire',
      icon: 'ri-store-line',
      path: '/distributor/login',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      hoverBg: 'hover:bg-green-100',
    },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 cursor-pointer">
              <img
                src="https://static.readdy.ai/image/3e6f353e8dbbec4f9360c3b978f4989c/d3b34cfe29fc668c336af62415ef516b.png"
                alt="ADF Biomédical"
                className="h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                    isScrolled
                      ? 'text-[#1A1A1A] hover:text-[#E31B23]'
                      : 'text-white hover:text-[#E31B23]'
                  } ${location.pathname === link.path ? 'text-[#E31B23]' : ''}`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/inscription"
                className="px-6 py-2.5 bg-[#E31B23] text-white rounded-lg font-medium text-sm hover:bg-[#C41820] transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Inscription
              </Link>
              <div className="relative">
                <button
                  onClick={() => setShowLoginMenu(!showLoginMenu)}
                  className={`w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-300 cursor-pointer ${
                    isScrolled
                      ? 'text-[#1A1A1A] hover:bg-gray-100'
                      : 'text-white hover:bg-white/10'
                  }`}
                  title="Connexion"
                >
                  <i className="ri-login-box-line text-xl"></i>
                </button>

                {/* Login Dropdown Menu */}
                {showLoginMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowLoginMenu(false)}
                    ></div>
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                      <div className="p-4 bg-gradient-to-r from-[#E31B23] to-[#C41820]">
                        <h3 className="text-white font-semibold text-lg">Choisissez votre espace</h3>
                        <p className="text-white/90 text-sm mt-1">Connectez-vous à votre compte</p>
                      </div>
                      <div className="p-2">
                        {loginOptions.map((option) => (
                          <Link
                            key={option.path}
                            to={option.path}
                            className={`flex items-start gap-4 p-4 rounded-lg transition-all duration-300 cursor-pointer ${option.hoverBg}`}
                          >
                            <div className={`w-12 h-12 flex items-center justify-center rounded-lg ${option.bgColor} flex-shrink-0`}>
                              <i className={`${option.icon} text-2xl ${option.color}`}></i>
                            </div>
                            <div className="flex-1">
                              <h4 className={`font-semibold ${option.color} mb-1`}>{option.title}</h4>
                              <p className="text-sm text-gray-600">{option.description}</p>
                            </div>
                            <i className="ri-arrow-right-line text-gray-400 text-lg mt-3"></i>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 cursor-pointer ${
                isScrolled ? 'text-[#1A1A1A]' : 'text-white'
              }`}
              aria-label="Menu"
            >
              <span
                className={`w-6 h-0.5 bg-current transition-all duration-300 ${
                  isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''
                }`}
              ></span>
              <span
                className={`w-6 h-0.5 bg-current transition-all duration-300 ${
                  isMobileMenuOpen ? 'opacity-0' : ''
                }`}
              ></span>
              <span
                className={`w-6 h-0.5 bg-current transition-all duration-300 ${
                  isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''
                }`}
              ></span>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}

      {/* Mobile Menu Drawer */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-80 bg-white z-50 transform transition-transform duration-300 lg:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <img
              src="https://static.readdy.ai/image/3e6f353e8dbbec4f9360c3b978f4989c/d3b34cfe29fc668c336af62415ef516b.png"
              alt="ADF Biomédical"
              className="h-10 w-auto"
            />
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="w-10 h-10 flex items-center justify-center text-[#1A1A1A] hover:text-[#E31B23] cursor-pointer"
              aria-label="Fermer le menu"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          {/* Mobile Menu Links */}
          <div className="flex-1 overflow-y-auto py-6">
            <div className="flex flex-col space-y-1 px-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 text-base font-medium rounded-lg transition-colors duration-300 cursor-pointer ${
                    location.pathname === link.path
                      ? 'bg-[#E31B23]/10 text-[#E31B23]'
                      : 'text-[#1A1A1A] hover:bg-gray-100'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Mobile Login Options */}
              <div className="pt-4 mt-4 border-t border-gray-200">
                <p className="px-4 text-xs font-semibold text-gray-500 uppercase mb-3">Connexion</p>
                {loginOptions.map((option) => (
                  <Link
                    key={option.path}
                    to={option.path}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-300 cursor-pointer ${option.hoverBg}`}
                  >
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${option.bgColor}`}>
                      <i className={`${option.icon} text-lg ${option.color}`}></i>
                    </div>
                    <div>
                      <p className={`font-medium text-sm ${option.color}`}>{option.title}</p>
                      <p className="text-xs text-gray-500">{option.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile Menu Footer */}
          <div className="p-6 border-t border-gray-200">
            <Link
              to="/inscription"
              className="block w-full px-6 py-3 bg-[#E31B23] text-white text-center rounded-lg font-medium hover:bg-[#C41820] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Inscription
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
